/**
 * @file Hook for determining what to show on Feed page
 */
import { Topic } from '../../../../../generated/graphql';
import useParamTopicId from '../../../../../hooks/router/params/useParamTopicId';

import useIsLeftSidebarOpen from '../../../../../hooks/router/search/useIsLeftSidebarOpen';
import useCurrentPath from '../../../../../hooks/useCurrentPath';
import useIsMobile from '../../../../../hooks/useIsMobile';
import {
  createFeedMessageDetailsActivityEditPath,
  createFeedMessageDetailsActivityPath,
  createFeedMessageDetailsEditPath,
  createFeedMessageDetailsPath,
  createFeedPath,
  createFeedTopicDetailsEditPath,
  createFeedTopicDetailsPath,
} from '../../../../paths/feed';

import useShouldShowComposeMain from './useShouldShowComposeMain';

type UseFeedParamsReturn = {
  isFeed: boolean;
  isSidebarOpen: boolean;
  shouldRedirect: boolean;
  shouldRenderMessageDetails: boolean;
  shouldShowCompose: boolean;
  shouldShowFeed: boolean;
  shouldShowRightSidebarDesktop: boolean;
  shouldShowRightSidebarMobile: boolean;
  topicId: Topic['id'] | null;
};

const showMessageDetailsFor: (string | null)[] = [
  createFeedMessageDetailsPath(),
  createFeedMessageDetailsEditPath(),
  createFeedMessageDetailsActivityEditPath(),
];

const showForDesktop: (string | null)[] = [
  createFeedMessageDetailsPath(),
  createFeedMessageDetailsEditPath(),
  createFeedTopicDetailsPath(),
  createFeedTopicDetailsEditPath(),
  createFeedMessageDetailsActivityPath(),
  createFeedMessageDetailsActivityEditPath(),
];

const showForMobile: (string | null)[] = [
  createFeedTopicDetailsPath(),
  createFeedTopicDetailsEditPath(),
];

/**
 * Until we are sure all users are using the new route for PostDetails
 * we leave this route and redirect to new route
 *
 * @todo Remove after there are no more visits to activity pages
 */
const redirectFor: (string | null)[] = [createFeedMessageDetailsActivityPath()];

/**
 * Hook for determining what to show on Feed page
 * Including:
 * - Left sidebar
 * - Right sidebar
 * Along with giving info about the current topic ID
 *
 * @returns Info about the feed page
 */
const useFeedParams = (): UseFeedParamsReturn => {
  const currentPath = useCurrentPath();
  const isMobile = useIsMobile();
  const isSidebarOpen = useIsLeftSidebarOpen();
  const topicId = useParamTopicId();
  const shouldShowCompose = useShouldShowComposeMain();

  const shouldShowRightSidebarDesktop = showForDesktop.includes(currentPath);
  const shouldShowRightSidebarMobile = showForMobile.includes(currentPath);
  const isDetails = currentPath?.includes(createFeedTopicDetailsPath());
  const isFeed = currentPath === createFeedPath();

  const shouldShowFeed =
    (shouldShowCompose === false && isDetails === false) || isMobile === false;

  /**
   * @todo Remove after there are no more visits to activity pages
   */
  const shouldRedirect = redirectFor.includes(currentPath);

  /**
   * Decide if messageDetails should be rendered.
   * Based on current path and whether app is rendered on mobile
   *
   * @returns Whether messageDetails should be rendered
   */
  const shouldRenderMessageDetails =
    showMessageDetailsFor.includes(currentPath) && isMobile;

  return {
    isFeed,
    isSidebarOpen,
    shouldRedirect,
    shouldRenderMessageDetails,
    shouldShowCompose,
    shouldShowFeed,
    shouldShowRightSidebarDesktop,
    shouldShowRightSidebarMobile,
    topicId,
  };
};

export default useFeedParams;
